import { cn } from '@/lib/cn'

/* The logo is real brand artwork and the guideline is strict about it:
 * never redraw, never recolour, never put a shadow on it (p.11). So the only
 * choice a caller makes is which of the delivered files to draw, and this
 * component's whole job is to map a variant name onto one of them.
 *
 * ── What this used to do, and why it was wrong ──────────────────────────────
 *
 * Every variant resolved to one of TWO files — `logo.svg` and `logo-white.svg`,
 * both of which are the mark on its own — on the stated grounds that "there is
 * no wordmark lockup in the product UI". That was never true of the callers:
 * the sidebar asked for `lockup-horizontal-light-text` and the login screen for
 * `lockup-horizontal-dark-text`, and both quietly got the bare mark. The eight
 * lockup files have been sitting in `public/brand/` the whole time.
 *
 * The two chrome callers have since chosen the mark ON PURPOSE — sidebar
 * `mark-white`, login `mark-blue` — which is not a return to that defect: the
 * defect was a variant name that did not identify its artwork, and every name
 * here still resolves to exactly one file. The document surfaces
 * (`OfferPreviewSheet`, and `platform/docs` server-side) keep the lockup.
 *
 * It also had a consequence outside this file. `platform/docs/pdf.go` refused to
 * draw a house mark on documents partly BECAUSE of this collapse — "Logo.tsx
 * collapses all eight variant names onto two files" was the evidence that a
 * variant could not identify artwork. It now identifies exactly one file.
 */

export type LogoVariant =
  | 'lockup-horizontal-dark-text'
  | 'lockup-horizontal-light-text'
  | 'lockup-horizontal-blue'
  | 'lockup-horizontal-white'
  | 'lockup-vertical-dark-text'
  | 'lockup-vertical-light-text'
  | 'mark-blue'
  | 'mark-white'

/* One file per variant, named exactly as `public/brand/` holds them. A table
   rather than a template literal on the variant, so that a variant added to the
   union without its artwork fails to compile instead of resolving to a 404 that
   renders as a torn image on the login screen. */
const ARTWORK: Record<LogoVariant, string> = {
  'lockup-horizontal-dark-text': '/brand/asa-lockup-horizontal-dark-text.svg',
  'lockup-horizontal-light-text': '/brand/asa-lockup-horizontal-light-text.svg',
  'lockup-horizontal-blue': '/brand/asa-lockup-horizontal-blue.svg',
  'lockup-horizontal-white': '/brand/asa-lockup-horizontal-white.svg',
  'lockup-vertical-dark-text': '/brand/asa-lockup-vertical-dark-text.svg',
  'lockup-vertical-light-text': '/brand/asa-lockup-vertical-light-text.svg',
  'mark-blue': '/brand/asa-mark-blue.svg',
  'mark-white': '/brand/asa-mark-white.svg',
}

export interface LogoProps {
  variant?: LogoVariant
  /** Rendered height in px. The width follows the artwork's aspect ratio. */
  height?: number
  className?: string
}

/* Base UI (`@base-ui/react`): no primitive. Static presentational markup. */
export function Logo({
  variant = 'lockup-horizontal-dark-text',
  height = 24,
  className,
}: LogoProps) {
  return (
    <img
      src={ARTWORK[variant] ?? ARTWORK['lockup-horizontal-dark-text']}
      alt="ASA Gateway"
      height={height}
      style={{ height }}
      className={cn('block w-auto', className)}
    />
  )
}
